import type { AddressRecord, StopPlanRecord, StopRecord } from "./driver-api.ts";
import { kilometres } from "./pickup.ts";
import type { ChipClass } from "./shift-readiness.ts";

/**
 * `Multi-stop job` — the plan a courier is carrying, as the design draws it.
 *
 * The design's screen, value for value from `15 WP14 Driver Onboarding and Earnings.dc.html`:
 *
 *     multi:{l:'Multi-stop job',map:1,on:1,chip:['active','stop 2 of 5'],
 *       title:'Oyster Bay, Plot 44',
 *       sub:'Then 3 more drops · 6.2 km left',
 *       rows:[['Stop 1','Masaki · delivered 10:41'],
 *             ['Stop 2','Oyster Bay · now'],
 *             ['Stops 3–5','Msasani, Mikocheni, Mbezi']],
 *       btns:[['Arrived at stop 2','where_to_vote',1,'slide']]}
 *
 * `tests/contract/mobile-screen-support.test.ts` had this screen **blocked** on one thing, and it
 * was not a field:
 *
 *   > the stop plan this driver is carrying — `POST /v1/tenants/{tenantId}/stop-plans` exists;
 *   > there is no `GET`
 *
 * That wording is the sweep's and it is half right. A `GET` did exist and was public — it is keyed
 * on a `planId` the handset never sees, which is the `GET /v1/sessions` trap `CLAUDE.md` section 4
 * names, arriving from the other direction. ADR 0040 added the listing a courier can actually ask:
 * `GET /v1/tenants/{tenantId}/drivers/{driverId}/stop-plans`, addressed by the courier's own
 * `driverId` and bounded by the verified subject stored on the row.
 *
 * ## Every value on this screen, and where it comes from
 *
 * | Drawn                               | Source                                                   |
 * | ----------------------------------- | -------------------------------------------------------- |
 * | `stop 2 of 5`                       | the plan's own stops: the first unfinished one, of all    |
 * | `Oyster Bay, Plot 44`               | GEO-02's `formattedAddress` for that stop's `addressId`   |
 * | `Then 3 more drops`                 | the stops after it                                        |
 * | `6.2 km left`                       | GEO-04 summed over the remaining legs — see `distanceLeft` |
 * | `Masaki · delivered 10:41`          | GEO-02's `locality` and the stop's own `completedAt`      |
 * | `Arrived at stop 2`                 | TRP-04's `arrive` transition on that stop                 |
 *
 * Nothing here is composed by this application that a published read already answers: the plan is
 * one read, each place is one address read keyed on an identifier the plan publishes (ADR 0036), and
 * the distance is GEO-04 over coordinates GEO-02 stored. The same three-service shape `4 To pickup`
 * uses, and `src/driver-api.ts` carries the rule it is allowed under.
 */

/** The scopes this screen spends, each named once so the derived client grant can see it. */
export const STOP_PLAN_READ_SCOPE = "trip:stops:self";
export const STOP_TRANSITION_SCOPE = "trip:stops:driver:transition";

/**
 * The stop a courier is working, and the ones on either side of it.
 *
 * "Working" is the first stop that is neither `completed` nor `skipped`, taken in `sequence` order
 * rather than in array order — the array is whatever jsonb gave back, and `reorder` renumbers
 * sequences without reordering the document. A plan every stop of which is finished has no current
 * stop, which is a real state: the courier has delivered everything and TRP-04 accepts no further
 * transition.
 */
export interface StopProgress {
  readonly ordered: readonly StopRecord[];
  /** The stop being worked, or undefined when every stop is finished. */
  readonly current: StopRecord | undefined;
  /** 1-based position of the current stop among all of them — the design's `2` of `2 of 5`. */
  readonly position: number | undefined;
  readonly total: number;
  /** The stops after the current one — the design's `3 more drops`. */
  readonly remaining: readonly StopRecord[];
  /** The stops already finished, in order, each of which the design gives its own row. */
  readonly finished: readonly StopRecord[];
}

export function stopProgress(plan: StopPlanRecord): StopProgress {
  const ordered = [...plan.stops].sort((left, right) => left.sequence - right.sequence);
  const at = ordered.findIndex((stop) => stop.status !== "completed" && stop.status !== "skipped");
  const current = at === -1 ? undefined : ordered[at];
  return {
    ordered,
    current,
    position: at === -1 ? undefined : at + 1,
    total: ordered.length,
    remaining: at === -1 ? [] : ordered.slice(at + 1),
    finished: at === -1 ? ordered : ordered.slice(0, at),
  };
}

/**
 * Which of a courier's plans this screen is about, when the listing answered more than one.
 *
 * The plan with work left in it, preferring the one furthest through — a courier who has arrived at
 * a stop is mid-delivery on that plan, and showing them a plan they have not started would be
 * showing them the wrong job. A courier with only finished plans is shown the last of them rather
 * than nothing, because "you have finished" is a truer screen than "you have no plan".
 *
 * Deterministic for a given page, which matters because this is a tie-break rather than an ordering
 * the platform publishes: TRP-04 pages over `planId` and nothing on a plan says which job a
 * dispatcher considers current. Recorded as Q-141.
 */
export function planInProgress(plans: readonly StopPlanRecord[]): StopPlanRecord | undefined {
  const unfinished = plans.filter((plan) => stopProgress(plan).current !== undefined);
  const arrived = unfinished.filter((plan) => plan.stops.some((stop) => stop.status === "arrived"));
  return arrived[0] ?? unfinished[0] ?? plans.at(-1);
}

/**
 * `['active','stop 2 of 5']` — the design's chip, which is the plan's own progress.
 *
 * `active` is the design's class for a courier mid-job, and its glyph is the half-filled ring. The
 * two states the design does not draw are a finished plan and a plan whose every stop was skipped,
 * and both take this build's words rather than the nearest confident neighbour: a `done` chip over
 * a plan nobody delivered anything on would be a state shown as its neighbour, which is the one
 * thing this application's vocabulary exists to avoid.
 */
export function planChip(progress: StopProgress): {
  readonly class: ChipClass;
  readonly label: string;
} {
  if (progress.position === undefined) {
    const delivered = progress.ordered.some((stop) => stop.status === "completed");
    return delivered
      ? { class: "done", label: "all stops done" }
      : { class: "closed", label: "all stops skipped" };
  }
  return {
    class: "active",
    label: `stop ${String(progress.position)} of ${String(progress.total)}`,
  };
}

/** True when the chip is in the one state the design draws. */
export function planChipIsDesigned(progress: StopProgress): boolean {
  return progress.position !== undefined;
}

/**
 * `Oyster Bay, Plot 44` — the current stop as a place, from GEO-02.
 *
 * `formattedAddress` and not `locality`: the design's headline is a street line and its rows are
 * localities, and GEO-02 publishes both. A courier needs the door on the headline and the
 * neighbourhood in the list, which is the distinction the design is drawing by using two shapes.
 *
 * The absence is the whole title rather than part of it. `8 Proof` stops its sentence where the
 * platform's knowledge stops because the sentence has a stem; this one has none — a place either
 * resolved or it did not — so the title says that in this build's words and the mark goes on the
 * row instead.
 */
export function planTitle(input: {
  readonly progress: StopProgress;
  readonly addresses: ReadonlyMap<string, AddressRecord>;
}): { readonly text: string; readonly designed: boolean } {
  const current = input.progress.current;
  if (current === undefined) return { text: ALL_STOPS_DONE_TITLE, designed: false };
  const place = input.addresses.get(current.addressId);
  if (place === undefined) return { text: STOP_PLACE_UNREADABLE_TITLE, designed: false };
  return { text: place.formattedAddress, designed: true };
}

/**
 * `Then 3 more drops · 6.2 km left` — the design's sub-line, composed from what answered.
 *
 * Two clauses, dropped independently, exactly as `4 To pickup`'s three are: a courier who can see
 * how many drops are left and not how far is better served than one who sees neither. With both the
 * sentence is the design's word for word, including its singular — `1 more drop`, because
 * `1 more drops` is the kind of thing that makes a product look unfinished.
 *
 * `No more drops` is the design-less case and is this build's wording: the current stop is the last
 * one, so "Then 0 more drops" would be arithmetic rather than a sentence.
 */
export function planSubLine(input: {
  readonly progress: StopProgress;
  readonly distanceMetresLeft: number | undefined;
}): { readonly text: string; readonly designed: boolean } {
  const drops = input.progress.remaining.length;
  const parts: string[] = [];
  if (input.progress.current === undefined) parts.push("Nothing left to deliver");
  else if (drops === 0) parts.push("Last drop");
  else parts.push(`Then ${String(drops)} more ${drops === 1 ? "drop" : "drops"}`);
  if (input.distanceMetresLeft !== undefined) {
    parts.push(`${kilometres(input.distanceMetresLeft)} left`);
  }
  return {
    text: parts.join(" · "),
    designed:
      input.distanceMetresLeft !== undefined && drops > 0 && input.progress.current !== undefined,
  };
}

/** One key/value row of the design's `rows` box, with `null` meaning the value has no source. */
export interface PlanRow {
  readonly key: string;
  readonly value: string | null;
  readonly because?: string;
}

/**
 * The design's three rows, generalised to a plan of any length without changing what it draws.
 *
 * The design's sample is a five-stop plan at stop 2 and it draws three rows — the stop behind, the
 * stop in hand, and the rest in one line. So the shape is: **one row per finished stop, one for the
 * current stop, and one grouping everything after it.** For the sample that is exactly the design's
 * three; for a plan at stop 4 of 5 it is five rows, which the sheet clips as the design's own
 * `overflow:hidden` clips `Vehicle`'s fourth document. Q-122 covers that and this screen does not
 * add a scroll container the design does not have.
 *
 * `Masaki · delivered 10:41` is GEO-02's `locality` and the stop's own `completedAt`, in the
 * **device's** timezone — no tenant timezone is published anywhere on this platform, which is Q-65
 * and is the same reading `7 Document expiring` takes of its hour.
 *
 * `Stops 3–5` uses an en dash, which is the design's own character.
 */
export function planRows(input: {
  readonly progress: StopProgress;
  readonly addresses: ReadonlyMap<string, AddressRecord>;
}): readonly PlanRow[] {
  const { progress, addresses } = input;
  const label = (stop: StopRecord): string | null => {
    const place = addresses.get(stop.addressId);
    return place === undefined ? null : (place.locality ?? place.formattedAddress);
  };
  const rows: PlanRow[] = [];

  for (const stop of progress.finished) {
    const place = label(stop);
    const when =
      stop.status === "completed"
        ? `delivered ${clockTime(stop.completedAt)}`
        : (stop.skipReason ?? "skipped");
    rows.push(
      place === null
        ? { key: stopKey(stop), value: null, because: STOP_PLACE_UNREADABLE }
        : { key: stopKey(stop), value: `${place} · ${when}` },
    );
  }

  if (progress.current !== undefined) {
    const place = label(progress.current);
    /*
     * `now` for a stop not yet reached and `here` for one already arrived at.
     *
     * The design draws `Oyster Bay · now` on a pending stop. `arrived` is a state TRP-04 publishes
     * and the design does not draw, and it is the state pressing this screen's own slider creates —
     * so the screen has to be able to say what it just did rather than keep printing `now`.
     */
    const when = progress.current.status === "arrived" ? "here now" : "now";
    rows.push(
      place === null
        ? { key: stopKey(progress.current), value: null, because: STOP_PLACE_UNREADABLE }
        : { key: stopKey(progress.current), value: `${place} · ${when}` },
    );
  }

  if (progress.remaining.length > 0) {
    const first = progress.remaining[0]!;
    const last = progress.remaining.at(-1)!;
    const key =
      progress.remaining.length === 1
        ? stopKey(first)
        : `Stops ${String(first.sequence)}–${String(last.sequence)}`;
    const places = progress.remaining.map(label);
    rows.push(
      places.some((place) => place === null)
        ? { key, value: null, because: STOP_PLACE_UNREADABLE }
        : { key, value: places.join(", ") },
    );
  }

  return rows;
}

/** `Stop 2` — the design's own row key, from the stop's published sequence. */
function stopKey(stop: StopRecord): string {
  return `Stop ${String(stop.sequence)}`;
}

/**
 * `10:41` from a stop's `completedAt`, in this device's timezone.
 *
 * Two digits either side and a 24-hour clock, which is the design's own `10:41` and also the only
 * format that does not need a locale nobody published. `undefined` is drawn as the design's own
 * absence word rather than as a blank, because a completed stop with no recorded instant is a real
 * row and an empty half of a sentence reads as a rendering fault.
 */
export function clockTime(at: string | null): string {
  if (at === null) return "time not recorded";
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return "time not recorded";
  return `${String(when.getHours()).padStart(2, "0")}:${String(when.getMinutes()).padStart(2, "0")}`;
}

/**
 * `Arrived at stop 2` — the design's one button, and what it transitions.
 *
 * `arrive`, which is TRP-04's own first action and the only one its domain accepts on a pending
 * stop: `moveStop` refuses an `arrive` on anything but the next pending stop, so the label and the
 * command cannot drift apart. The design's own notes say "slide-to-confirm for accept, arrive,
 * start and complete" — arriving is the second of the four.
 */
export const ARRIVE_AT_STOP = "arrive" as const;

/** The design's label, which names the stop it acts on. */
export function arriveLabel(progress: StopProgress): string {
  return progress.position === undefined
    ? "Arrived at this stop"
    : `Arrived at stop ${String(progress.position)}`;
}

/**
 * Why `Arrived at stop 2` cannot be pressed, when it cannot.
 *
 * `undefined` means pressable. Anything else is said on the press, because the design draws one
 * slider here and nothing may be removed — a plan whose stops are all finished keeps its slider and
 * explains itself rather than losing it.
 */
export function arriveUnavailable(progress: StopProgress): string | undefined {
  if (progress.current === undefined) {
    return "Every stop on this plan is finished, so TRP-04 accepts no further transition on it.";
  }
  if (progress.current.status === "arrived") {
    return "You've already told us you're at this stop. Completing it is the next step, and no screen draws it yet.";
  }
  return undefined;
}

/** Said in the row when GEO-02 could not name a stop's place. */
export const STOP_PLACE_UNREADABLE = "This stop's address couldn't be read";

/** Said as the headline when the stop in hand has no readable place. */
export const STOP_PLACE_UNREADABLE_TITLE = "We can't name this stop's place";

/** Said as the headline when every stop is finished, which the design does not draw. */
export const ALL_STOPS_DONE_TITLE = "Every stop on this plan is done";

/** Said when the courier has no plan at all, which is not a failure. */
export const NO_PLAN_TITLE = "You're not carrying a multi-stop job";
export const NO_PLAN_SUB =
  "Nothing is assigned to you right now. This screen fills in when a multi-stop job is.";

/** Said when this application does not know which courier it is for. */
export const NO_COURIER_TITLE = "We don't know which courier you are";
export const NO_COURIER_SUB =
  "Your sign-in carries no driver_id, and nothing published says a token's subject is a courier's id, so this app won't guess one.";

/** Said after a 409 on the slider, which is a read of the current state rather than a retry. */
export const ARRIVAL_CONFLICT_TITLE = "This plan changed while you were looking at it";
export const ARRIVAL_CONFLICT_SUB =
  "Your arrival wasn't applied, and nothing is lost. The stops below are the plan as it stands now.";
